import { acceptHMRUpdate, defineStore } from 'pinia';
import { useFlowStore } from '@/stores/useFlowStore';
import type { GtmItem, GTMState } from '@/stores/schemas';

export const useGTMStore = defineStore('gtm', {
	state: (): GTMState => ({
		resort: undefined,
		last_items: [],
	}),

	actions: {
		sendEvent(
			event: string,
			extra_variables: { [key: string]: string | number | boolean } = {},
			alwaysSend = false,
			filterProducts = true,
		) {
			const flowstore = useFlowStore();

			// Reset the ecommerce events
			window.dataLayer.push({ ecommerce: null });

			const eventData = {
				event,
				booking_for_resort: this.$state.resort,
				ecommerce: {
					currency: 'EUR',
					...extra_variables,
					...(filterProducts
						? this.getItemDifferences(flowstore.checkout.meta.gtm.items)
						: { value: flowstore.checkout.meta.gtm.value, items: flowstore.checkout.meta.gtm.items }),
				},
			};

			// We only want to push this when there are new products added to the items list,
			// or whenever we send to always send the event.
			if (alwaysSend || eventData.ecommerce.items.length > 0) {
				window.dataLayer.push(eventData);
			}
		},

		/**
		 * Calculates the difference between two item arrays based on item_id.
		 * Returns an array containing only the products that are newly added
		 * or have an increased quantity.
		 *
		 * Deleted products are not a problem when detecting changes, we also have to recalculate new prices
		 */
		getItemDifferences(items: GtmItem[]) {
			const oldMap = new Map(this.last_items.map(({ item_id, quantity }) => [item_id, quantity]));
			let addedPrice = 0;

			this.last_items = items;

			const differences = items
				.map((item) => {
					const diff = item.quantity - (oldMap.get(item.item_id) ?? 0);

					if (diff > 0) {
						addedPrice += item.price * diff;
						return { ...item, quantity: diff };
					}

					return null;
				})
				.filter(Boolean);

			return {
				value: addedPrice,
				items: differences,
			};
		},
	},
});

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useGTMStore, import.meta.hot));
}
